import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Trash2, 
  Save, 
  Zap, 
  Settings2, 
  Monitor, 
  Tablet, 
  Smartphone, 
  Eye, 
  Code,
  Layout,
  Type,
  Image as ImageIcon,
  MousePointer2,
  ChevronRight,
  ChevronDown,
  Box,
  Layers,
  Sparkles,
  Search,
  Maximize2,
  RefreshCw,
  MoreVertical,
  Check,
  Undo2,
  Redo2,
  Download,
  Share2,
  Activity,
  Send,
  MessageSquare,
  History,
  Info,
  Edit3
} from 'lucide-react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'motion/react';
import { editSynthesis } from '../lib/gemini';
import { parseFiles } from '../lib/parser';
import { getPlanConfig, getUpgradeTrigger, UpgradeTriggerStore } from '../lib/pricing';
import { UpgradeModal } from '../components/UpgradeModal';
import { useTasks } from '../contexts/TaskContext';

// --- Types ---

import { handleFirestoreError } from '../lib/firebaseUtils';

interface ProjectState {
  id: string;
  name: string;
  activeBuildId?: string;
  designSystem?: any;
  architectState?: {
    sourceCode: string;
    updatedAt: any;
  };
}

export default function VisualEditor({ profile, lastBuild, setLastBuild }: { profile: any, lastBuild: any, setLastBuild: any }) {
  const { tasks, startTask, updateTask, completeTask, failTask } = useTasks();
  const refinementTask = tasks.refinement;

  const [searchParams] = useSearchParams();
  const [projectId, setProjectId] = useState<string | null>(searchParams.get('project'));
  const navigate = useNavigate();
  
  const [project, setProject] = useState<ProjectState | null>(null);
  const [sourceCode, setSourceCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [viewDevice, setViewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isSaving, setIsSaving] = useState(false);

  // Undo/Redo State
  const [historyStack, setHistoryStack] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const isProcessing = refinementTask.status === 'running';
  
  // Chat Editor State
  const [prompt, setPrompt] = useState('');
  const [history, setHistory] = useState<{ role: 'user' | 'assistant', content: string, thoughts?: string[] }[]>([]);
  const [activeThoughts, setActiveThoughts] = useState<string[]>([]);
  const [projectBuilds, setProjectBuilds] = useState<any[]>([]);

  useEffect(() => {
    if (!projectId) return;
    const q = query(
      collection(db, `projects/${projectId}/documents`),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    return onSnapshot(q, (snapshot) => {
      const builds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjectBuilds(builds);
    });
  }, [projectId]);

  const handleSelectBuild = (build: any) => {
    const files = parseFiles(build.content);
    setLastBuild({
      brief: build.brief || project?.name,
      files: Object.entries(files).map(([name, content]: [string, any]) => ({ name, content })),
      docId: build.id
    });
    toast.success(`Loaded build: ${build.id.slice(0, 8)}`);
  };

  useEffect(() => {
    if (lastBuild?.files?.length) {
      const indexFile = lastBuild.files.find((f: any) => f.name.toLowerCase() === 'index.html') || lastBuild.files[0];
      const code = indexFile.content;
      if (code !== sourceCode) {
        setSourceCode(code);
        setHistoryStack(prev => [...prev.slice(0, historyIndex + 1), code]);
        setHistoryIndex(prev => prev + 1);
      }
    }
  }, [lastBuild]);

  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }
    const projectRef = doc(db, 'projects', projectId);
    return onSnapshot(projectRef, 
      async (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as ProjectState;
          setProject({ id: snapshot.id, ...data });
          
          if (!sourceCode) {
            let initialCode = '';
            if (data.architectState?.sourceCode) {
              initialCode = data.architectState.sourceCode;
            } else if (data.activeBuildId) {
               try {
                 const docSnap = await getDoc(doc(db, `projects/${projectId}/documents`, data.activeBuildId));
                 if (docSnap.exists()) {
                   const buildData = docSnap.data() as any;
                   const files = parseFiles(buildData.content);
                   initialCode = files['index.html'] || buildData.content;
                 }
               } catch (err) {
                 console.error("Error fetching build doc:", err);
               }
            }
            if (initialCode) {
              setSourceCode(initialCode);
              setHistoryStack([initialCode]);
              setHistoryIndex(0);
            }
            setIsLoading(false);
          } else {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error("VisualEditor Project Listener Error:", error);
        setIsLoading(false);
        handleFirestoreError(error, 'watch', `projects/${projectId}`);
      }
    );
  }, [projectId]);

  const updateSourceCodeWithHistory = (newCode: string) => {
    const newStack = historyStack.slice(0, historyIndex + 1);
    newStack.push(newCode);
    if (newStack.length > 50) newStack.shift();
    setHistoryStack(newStack);
    setHistoryIndex(newStack.length - 1);
    setSourceCode(newCode);
  };

  const assembleHtml = (mainCode: string, allFiles: any[]) => {
    if (!mainCode.trim().toLowerCase().startsWith('<!doctype html>') && !mainCode.trim().toLowerCase().startsWith('<html')) {
        return mainCode;
    }

    let assembed = mainCode;

    // Inline CSS
    allFiles.forEach(file => {
      if (file.name.endsWith('.css')) {
        const linkTag = new RegExp(`<link[^>]*href=["'](?:./)?${file.name}["'][^>]*>`, 'gi');
        if (linkTag.test(assembed)) {
          assembed = assembed.replace(linkTag, `<style>${file.content}</style>`);
        } else {
          assembed = assembed.replace('</head>', `<style>${file.content}</style></head>`);
        }
      }
    });

    // Inline JS
    allFiles.forEach(file => {
      if (file.name.endsWith('.js')) {
        const scriptTag = new RegExp(`<script[^>]*src=["'](?:./)?${file.name}["'][^>]*>\\s*</script>`, 'gi');
        if (scriptTag.test(assembed)) {
          assembed = assembed.replace(scriptTag, `<script>${file.content}</script>`);
        } else {
          assembed = assembed.replace('</body>', `<script>${file.content}</script></body>`);
        }
      }
    });

    return assembed;
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setSourceCode(historyStack[prevIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < historyStack.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setSourceCode(historyStack[nextIndex]);
    }
  };

  const handleSave = async () => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        architectState: { sourceCode, updatedAt: serverTimestamp() }
      });
      toast.success("Editor State Synchronized");
    } catch (error) {
      toast.error("Sync Failed");
    } finally {
      setIsSaving(false);
    }
  };

  const [isPublishing, setIsPublishing] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState<UpgradeTriggerStore>(null);
  const plan = getPlanConfig(profile?.plan);
  const usage = {
    generationsUsed: profile?.generationsUsed || 0,
    activeProjects: profile?.projectCount || 0,
    hasAttemptedDeploy: false
  };

  const handlePublish = async () => {
    if (!projectId || !sourceCode || isPublishing) return;

    // Check deploy permission
    const trigger = getUpgradeTrigger(plan.id, usage, 'deploy');
    if (trigger) {
      setUpgradeTrigger(trigger);
      return;
    }

    setIsPublishing(true);
    const toastId = toast.loading("Synthesizing release for Ship engine...");
    try {
      // Create a unique deployment ID
      const deploymentId = `${projectId}_build_${Date.now().toString().slice(-6)}`;
      const liveUrl = `${window.location.origin}/pub/${deploymentId}`;

      // Parse files from sourceCode
      const parsedFilesDict = parseFiles(sourceCode);
      const fileArray = Object.entries(parsedFilesDict).map(([name, content]) => ({ name, content }));
      
      // Find the main entry point (usually index.html)
      const indexPage = fileArray.find(f => f.name.toLowerCase() === 'index.html') || fileArray[0];

      await setDoc(doc(db, 'deployments', deploymentId), {
        projectId,
        ownerId: auth.currentUser?.uid,
        ownerPlan: plan.id,
        title: `${project?.name} - Refined Release`,
        content: indexPage.content,
        files: fileArray,
        status: 'Live',
        deployedAt: serverTimestamp(),
        url: liveUrl,
        liveUrl: liveUrl
      });
      toast.success("Build pushed to Ship engine.");
      setTimeout(() => {
        navigate(`/studio/build?project=${projectId}`);
      }, 1500);
    } catch (error) {
      console.error("Publishing Failed:", error);
      toast.error("Publishing Failed");
    } finally {
      setIsPublishing(false);
      toast.dismiss(toastId);
    }
  };

  const handleChatEdit = async () => {
    if (!prompt.trim() || isProcessing) return;
    
    startTask('refinement', { prompt, projectId });
    setActiveThoughts(['Editor is initializing...', 'Parsing source architecture...']);
    setHistory(prev => [...prev, { role: 'user', content: prompt }]);
    const currentPrompt = prompt;
    setPrompt('');

    const toastId = toast.loading(`Editor is modifying source code (working in background)...`);
    
    // Simulate real-time thinking progress
    const thoughtInterval = setInterval(() => {
      const genericThoughts = [
        'Evaluating codebase integrity...',
        'Re-indexing visual primitives...',
        'Optimizing component performance...',
        'Synthesizing architectural logic...',
        'Validating logic flow...'
      ];
      setActiveThoughts(prev => {
        if (prev.length >= 6) return prev;
        const nextThought = genericThoughts[Math.floor(Math.random() * genericThoughts.length)];
        return Array.from(new Set([...prev, nextThought]));
      });
      
      updateTask('refinement', { 
        progress: Math.min(95, tasks.refinement.progress + 5) 
      });
    }, 1500);

    try {
      const result = await editSynthesis(
        currentPrompt, 
        sourceCode, 
        'app', 
        project?.designSystem
      );

      clearInterval(thoughtInterval);
      const reasoning = result.reasoning || [`Applied requested modifications.`];
      setActiveThoughts(reasoning);

      if (result.content) {
        const files = parseFiles(result.content);
        const newCode = files['index.html'] || result.content;
        updateSourceCodeWithHistory(newCode);
        completeTask('refinement', { newCode, reasoning });
        toast.success("Synthesis complete.");
      }
      
      setTimeout(() => {
        setHistory(prev => [...prev, { 
          role: 'assistant', 
          content: `Build refined.`,
          thoughts: reasoning
        }]);
        setActiveThoughts([]);
      }, 500);

    } catch (error) {
      clearInterval(thoughtInterval);
      console.error("Edit failed:", error);
      failTask('refinement', error instanceof Error ? error.message : 'Unknown error');
      toast.error("Synthesis refinement failed.");
      setHistory(prev => [...prev, { role: 'assistant', content: "I encountered an error while synthesizing those changes." }]);
      setActiveThoughts([]);
    } finally {
      toast.dismiss(toastId);
    }
  };

  const clearHistory = () => setHistory([]);

  return (
    <div className="flex h-screen bg-black text-zinc-100 font-sans selection:bg-[#0074D9] selection:text-white">
      <aside className="w-80 border-r border-zinc-900 bg-zinc-950 flex flex-col shrink-0">
        <div className="p-8 border-b border-zinc-900 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-sm font-mono tracking-widest text-white uppercase">editor</h1>
              <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">Visual Logic Engine</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 custom-scroll">
          <div className="h-full flex flex-col">
              <div className="flex-1 space-y-4 mb-4">
                {history.length === 0 && activeThoughts.length === 0 ? (
                  <div className="p-4 border border-[#222222] rounded-xl bg-[#0A0A0A] opacity-60">
                    <p className="text-[10px] font-mono text-zinc-500 uppercase leading-relaxed">
                      Initialize conversation to modify the entire application. 
                      Describe your vision, and the Editor will synthesize the logic.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((msg, i) => (
                      <div key={i} className={`space-y-2`}>
                        <div className={`p-4 rounded-xl border ${msg.role === 'user' ? 'bg-[#111111] border-[#333333] ml-4' : 'bg-[#001F3F]/20 border-[#0074D9]/20 mr-4'}`}>
                          <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-1">{msg.role === 'user' ? 'Input' : 'Editor'}</p>
                          <p className="text-xs text-zinc-300 leading-relaxed">{msg.content}</p>
                        </div>
                        {msg.thoughts && msg.thoughts.length > 0 && (
                          <div className="ml-4 pl-4 border-l border-zinc-900 space-y-1">
                            {msg.thoughts.map((t, ti) => (
                              <p key={ti} className="text-[9px] font-mono text-zinc-600 uppercase tracking-tight italic opacity-60">
                                {t}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {activeThoughts.length > 0 && (
                      <div className="p-4 rounded-xl border bg-black border-[#0074D9]/10 mr-4 space-y-3">
                        <div className="flex items-center gap-2 text-[#0074D9]">
                          <RefreshCw className="animate-spin" size={12} />
                          <span className="text-[10px] font-mono uppercase tracking-widest font-bold">Thinking Process</span>
                        </div>
                        <div className="space-y-2">
                          {activeThoughts.map((t, ti) => (
                            <motion.div 
                              key={ti}
                              initial={{ opacity: 0, x: -5 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="flex items-center gap-2"
                            >
                              <div className="w-1 h-1 bg-[#0074D9] rounded-full" />
                              <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-tighter">{t}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            
            <div className="space-y-3">
              <div className="relative">
                <textarea 
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleChatEdit();
                    }
                  }}
                  placeholder="Request changes to the build..."
                  className="w-full h-32 bg-[#0A0A0A] border border-[#222222] rounded-xl p-4 text-xs text-white focus:outline-none focus:border-[#0074D9] placeholder:text-zinc-700 resize-none font-sans"
                />
                <button 
                  onClick={handleChatEdit}
                  disabled={isProcessing || !prompt.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-[#0074D9] text-white rounded-lg hover:bg-[#0074D9]/80 disabled:opacity-50 transition-all font-bold"
                >
                  <Send size={14} />
                </button>
              </div>
              <div className="flex justify-between items-center px-1">
                <button onClick={clearHistory} className="text-[8px] font-mono text-zinc-700 uppercase hover:text-white flex items-center gap-1">
                  <History size={10} /> Clear Logs
                </button>
                <p className="text-[8px] font-mono text-zinc-800 uppercase italic">v3.0 Synthesis</p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-zinc-900 bg-zinc-950/50 flex flex-col gap-3">
          <button onClick={handleSave} disabled={isSaving} className="w-full h-12 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white hover:border-zinc-700 flex items-center justify-center gap-2 transition-all disabled:opacity-50">{isSaving ? <RefreshCw className="animate-spin" size={12} /> : <Save size={12} />}Sync State</button>
          <button onClick={handlePublish} disabled={isPublishing} className="w-full h-12 bg-[#0074D9] rounded-lg text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-white hover:bg-[#0074D9]/80 shadow-[0_0_20px_rgba(0,116,217,0.3)] flex items-center justify-center gap-2 transition-all disabled:opacity-50">Publish to Ship</button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 bg-black">
        <header className="h-16 border-b border-zinc-900 px-8 flex items-center justify-between bg-zinc-950/50 backdrop-blur-xl">
          <div className="flex items-center gap-6">
            <div className="flex items-center bg-zinc-900 rounded-lg p-1 border border-zinc-800">
              <button onClick={() => setViewDevice('desktop')} className={`p-2 rounded transition-all ${viewDevice === 'desktop' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}><Monitor size={16} /></button>
              <button onClick={() => setViewDevice('tablet')} className={`p-2 rounded transition-all ${viewDevice === 'tablet' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}><Tablet size={16} /></button>
              <button onClick={() => setViewDevice('mobile')} className={`p-2 rounded transition-all ${viewDevice === 'mobile' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}><Smartphone size={16} /></button>
            </div>
            <div className="h-6 w-px bg-zinc-900" />
            <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-zinc-800">
              <button 
                onClick={handleUndo} 
                disabled={historyIndex <= 0}
                className="p-2 rounded text-zinc-600 hover:text-zinc-400 disabled:opacity-30 transition-all"
                title="Undo"
              >
                <Undo2 size={16} />
              </button>
              <button 
                onClick={handleRedo} 
                disabled={historyIndex >= historyStack.length - 1}
                className="p-2 rounded text-zinc-600 hover:text-zinc-400 disabled:opacity-30 transition-all"
                title="Redo"
              >
                <Redo2 size={16} />
              </button>
            </div>
            <div className="h-6 w-px bg-zinc-900" />
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">Build Cache:</span>
              <select 
                onChange={(e) => {
                  const b = projectBuilds.find(build => build.id === e.target.value);
                  if (b) handleSelectBuild(b);
                }}
                value={lastBuild?.docId || ''}
                className="bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-white px-3 py-1 rounded-lg focus:outline-none focus:border-[#0074D9]"
              >
                <option value="" disabled>Select Version</option>
                {projectBuilds.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.id.slice(0, 8)} - {new Date(b.createdAt?.seconds * 1000).toLocaleTimeString()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </header>

        <div className="grow overflow-hidden bg-[#050505] flex justify-center items-center">
          {isLoading ? (
            <div className="flex flex-col items-center gap-4">
              <RefreshCw className="animate-spin text-[#0074D9]" size={32} />
              <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Synthesizing Preview Stage...</p>
            </div>
          ) : !sourceCode ? (
             <div className="flex flex-col items-center gap-6 p-12 text-center max-w-sm">
               <div className="w-16 h-16 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center">
                 <Zap className="text-zinc-600" size={32} />
               </div>
               <div className="space-y-2">
                 <h3 className="text-sm font-mono uppercase tracking-widest text-white">No Build Detected</h3>
                 <p className="text-[10px] text-zinc-500 font-sans leading-relaxed">Initials synthesis must be completed in the Build module before refinement can begin.</p>
               </div>
               <button 
                 onClick={() => navigate(`/studio/build?project=${projectId}`)}
                 className="px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white hover:border-zinc-600 transition-all"
               >
                 Go to Build Engine
               </button>
             </div>
          ) : (
            <div className={`h-full transition-all duration-300 shadow-2xl bg-white overflow-hidden ${viewDevice === 'desktop' ? 'w-full' : viewDevice === 'tablet' ? 'w-[768px]' : 'w-[375px]'}`}>
              <iframe 
                srcDoc={assembleHtml(sourceCode, lastBuild?.files || [])}
                title="Editor Preview"
                className="w-full h-full border-none"
                sandbox="allow-scripts allow-forms allow-popups allow-pointer-lock allow-same-origin"
                onLoad={(e) => {
                  const iframe = e.target as HTMLIFrameElement;
                  iframe.contentWindow?.focus();
                }}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
